import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useAppointMember } from '@/hooks/use-committee'
import { COMMITTEE_POSITION_LABELS, type CommitteePosition } from '@riverpark/types'
import type { MemberListItem } from '@riverpark/types'

const POSITIONS = Object.entries(COMMITTEE_POSITION_LABELS) as [CommitteePosition, string][]

interface AppointMemberModalProps {
  open: boolean
  onClose: () => void
  /** Pre-select a specific member (e.g. when opened from a member row) */
  preselectedMember?: Pick<MemberListItem, 'id' | 'name'> | null
  committeeMembers: Pick<MemberListItem, 'id' | 'name'>[]
}

export function AppointMemberModal({
  open,
  onClose,
  preselectedMember,
  committeeMembers,
}: AppointMemberModalProps) {
  const appointMember = useAppointMember()

  const [memberId, setMemberId] = useState('')
  const [position, setPosition] = useState<CommitteePosition | ''>('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setMemberId(preselectedMember?.id ?? '')
    setPosition('')
    setNotes('')
    setError(null)
  }, [open, preselectedMember?.id])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!memberId || !position) return
    setError(null)
    try {
      await appointMember.mutateAsync({
        memberId,
        position,
        ...(notes.trim() && { notes: notes.trim() }),
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create appointment')
    }
  }

  const selectedMemberName =
    preselectedMember?.name ?? committeeMembers.find((m) => m.id === memberId)?.name ?? null

  return (
    <Modal open={open} onClose={onClose} labelledBy="appoint-member-title">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
              Committee
            </p>
            <h2 id="appoint-member-title" className="font-serif text-xl text-ink">
              {selectedMemberName ? `Appoint ${selectedMemberName}` : 'Appoint to position'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      <form onSubmit={(e) => void handleSubmit(e)} className="flex min-h-0 flex-1 flex-col">
        <ModalBody className="space-y-4">
          {/* Member picker — only shown when not pre-selected */}
          {!preselectedMember && (
            <div className="space-y-1.5">
              <label className="font-sans text-sm font-medium text-ink" htmlFor="appoint-member">
                Committee member
              </label>
              <select
                id="appoint-member"
                value={memberId}
                onChange={(e) => setMemberId(e.target.value)}
                required
                className="flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
              >
                <option value="">Select a member…</option>
                {committeeMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Position picker */}
          <div className="space-y-1.5">
            <label className="font-sans text-sm font-medium text-ink" htmlFor="appoint-position">
              Position
            </label>
            <select
              id="appoint-position"
              value={position}
              onChange={(e) => setPosition(e.target.value as CommitteePosition | '')}
              required
              className="flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
            >
              <option value="">Select a position…</option>
              {POSITIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <p className="font-sans text-xs text-muted-foreground">
              Appointing automatically ends the current holder's term for this position.
            </p>
          </div>

          {/* Optional notes */}
          <div className="space-y-1.5">
            <label className="font-sans text-sm font-medium text-ink" htmlFor="appoint-notes">
              Notes <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <textarea
              id="appoint-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="e.g. Appointed at 2024 AGM"
              className="w-full rounded border border-input bg-card px-3.5 py-2.5 font-sans text-sm text-ink placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
            />
          </div>

          {error && (
            <p className="font-sans text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </ModalBody>

        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={appointMember.isPending} disabled={!memberId || !position}>
            Appoint
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
